import type { GetMessagesQueryVariables } from '#src/graphql/generated';
import type { EditorJsData } from './editorjs-types';

import { useApolloClient, useMutation } from '@apollo/client/react';
import {
  Popover,
  Portal,
  Button,
  Spinner,
  VStack,
  Textarea,
  Text,
  HStack,
  Box,
  DialogRoot,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogActionTrigger,
} from '@chakra-ui/react';
import React from 'react';
import { FiZap } from 'react-icons/fi';

import { useEditDocument } from './edit-document-context';
import { parseEditorJs, stringifyEditorJs } from './editorjs-types';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import { CenteredDialogContent } from '#src/components/ui/centered-dialog-content';
import { toaster } from '#src/components/ui/toaster';
import { EXECUTE_PROCESSOR } from '#src/graphql/mutations/execute-processor';
import { persistProcessorFailureAsDocumentRootMessage } from '#src/utils/persist-processor-failure-message';

const DOCUMENT_AI_EDIT_PROCESSOR_ID = 'edit-document-prompt';
const ASK_DOCUMENT_PROCESSOR_ID = 'ask-document-prompt';

/** Keep in sync with edit-document-prompt.ts DEFAULT_EDIT_SUMMARY_MESSAGE */
const FALLBACK_AI_EDIT_TOAST_DESCRIPTION =
  'AI edit applied to editor; save to persist.';

export interface PromptCommandBaseProps {
  /** Document ID - required for processing prompts */
  documentId?: string;
  /** Whether this is a new document (not yet saved) */
  isNewDocument: boolean;
  /** Optional tuned block id to scope the AI request. */
  focusBlockId?: string | null;
  /** Callback to set the editor value with the AI-edited document (user confirms and saves manually) */
  onValueUpdate: (value: EditorJsData | null) => void;
  /** Current editor JSON string so the server can apply edits on unsaved changes. */
  getEditorSnapshot?: () => string | null;
  /** OpenAI chat model id for this request; must be in the deployment allowlist. */
  model?: string;
  /**
   * Document message thread root to append this edit to in the chat log (server
   * `recordDocumentAiEditMessages`). Omit to start a new thread.
   */
  threadRootId?: string | null;
}

export function usePromptSubmit({
  documentId,
  isNewDocument,
  focusBlockId,
  onValueUpdate,
  getEditorSnapshot,
  model,
  threadRootId,
}: PromptCommandBaseProps) {
  const apolloClient = useApolloClient();
  const [executeProcessor, { loading: processingPrompt }] =
    useMutation(EXECUTE_PROCESSOR);

  const documentMessagesVariables = React.useMemo<GetMessagesQueryVariables>(
    () => ({
      input: {
        contextFilters: [{ name: 'documentId', value: documentId ?? '' }],
      },
    }),
    [documentId],
  );

  const disabled = processingPrompt || isNewDocument;

  const handleSubmit = React.useCallback(
    async (promptText: string): Promise<boolean> => {
      if (!promptText.trim()) return false;
      if (!documentId) {
        toaster.create({
          title: 'Error',
          description: 'Save the document first to use AI modifications.',
          type: 'error',
        });
        return false;
      }

      try {
        const snap = getEditorSnapshot?.() ?? null;
        const { data } = await executeProcessor({
          variables: {
            input: {
              processorId: DOCUMENT_AI_EDIT_PROCESSOR_ID,
              variables: {
                input: promptText.trim(),
                documentId,
                ...(snap && snap.length > 0
                  ? { currentEditorValue: snap }
                  : {}),
                ...(focusBlockId ? { focusBlockId } : {}),
                ...(model ? { model } : {}),
                ...(threadRootId?.trim()
                  ? { threadRootId: threadRootId.trim() }
                  : {}),
              },
            },
          },
        });

        const result = data?.executeProcessor;
        const persistDocError = async (
          message: string,
          instanceId?: string | null,
        ) => {
          if (!documentId) return;
          const persist = await persistProcessorFailureAsDocumentRootMessage(
            apolloClient,
            {
              documentId,
              errorMessage: message,
              processorInstanceId: instanceId,
              getMessagesVariables: documentMessagesVariables,
            },
          );
          if (!persist.ok) {
            toaster.create({
              title: 'Could not save error to messages',
              description: persist.error,
              type: 'warning',
            });
          }
        };

        if (!result?.success) {
          await persistDocError(
            result?.error ?? 'Processor failed',
            result?.instanceId,
          );
          return false;
        }
        if (result.result == null) {
          await persistDocError(
            'Processor returned no result',
            result?.instanceId,
          );
          return false;
        }
        const inner = result.result as {
          success?: boolean;
          error?: string;
          value?: unknown;
          message?: unknown;
        };
        if (inner.success === false) {
          await persistDocError(
            inner.error ?? 'Request failed',
            result?.instanceId,
          );
          return false;
        }
        const value = typeof inner.value === 'string' ? inner.value : undefined;
        if (typeof value === 'string') {
          const parsed = parseEditorJs(value);
          if (!parsed) {
            await persistDocError(
              'Processor did not return valid Editor.js JSON',
              result?.instanceId,
            );
            return false;
          }
          onValueUpdate(parsed);
          const summary =
            typeof inner.message === 'string' ? inner.message.trim() : '';
          toaster.create({
            title: 'Success',
            description: summary || FALLBACK_AI_EDIT_TOAST_DESCRIPTION,
          });
          return true;
        }
        await persistDocError(
          'Processor did not return document value',
          result?.instanceId,
        );
        return false;
      } catch (error) {
        const description =
          error instanceof Error ? error.message : 'Unknown error';
        if (documentId) {
          const persist = await persistProcessorFailureAsDocumentRootMessage(
            apolloClient,
            {
              documentId,
              errorMessage: description,
              processorInstanceId: undefined,
              getMessagesVariables: documentMessagesVariables,
            },
          );
          if (!persist.ok) {
            toaster.create({
              title: 'Could not save error to messages',
              description: persist.error,
              type: 'warning',
            });
          }
        } else {
          toaster.create({
            title: 'Error',
            description: `Error applying AI edit: ${description}`,
            type: 'error',
          });
        }
        return false;
      }
    },
    [
      apolloClient,
      documentId,
      documentMessagesVariables,
      focusBlockId,
      model,
      threadRootId,
      onValueUpdate,
      getEditorSnapshot,
      executeProcessor,
    ],
  );

  return { handleSubmit, disabled, processingPrompt };
}

export interface UseAskSubmitParams {
  documentId?: string;
  isNewDocument: boolean;
}

export function useAskSubmit({
  documentId,
  isNewDocument,
}: UseAskSubmitParams) {
  const apolloClient = useApolloClient();
  const [executeProcessor, { loading: processingPrompt }] =
    useMutation(EXECUTE_PROCESSOR);
  const [askResult, setAskResult] = React.useState<string | null>(null);

  const documentMessagesVariables = React.useMemo<GetMessagesQueryVariables>(
    () => ({
      input: {
        contextFilters: [{ name: 'documentId', value: documentId ?? '' }],
      },
    }),
    [documentId],
  );

  const disabled = processingPrompt || isNewDocument;

  const handleAskSubmit = React.useCallback(
    async (promptText: string) => {
      if (!promptText.trim()) return;
      if (!documentId) {
        toaster.create({
          title: 'Error',
          description: 'Save the document first to use AI.',
          type: 'error',
        });
        return;
      }

      try {
        const { data } = await executeProcessor({
          variables: {
            input: {
              processorId: ASK_DOCUMENT_PROCESSOR_ID,
              variables: {
                input: promptText.trim(),
                documentId,
              },
            },
          },
        });

        const result = data?.executeProcessor;
        const persistDocError = async (
          message: string,
          instanceId?: string | null,
        ) => {
          if (!documentId) return;
          const persist = await persistProcessorFailureAsDocumentRootMessage(
            apolloClient,
            {
              documentId,
              errorMessage: message,
              processorInstanceId: instanceId,
              getMessagesVariables: documentMessagesVariables,
            },
          );
          if (!persist.ok) {
            toaster.create({
              title: 'Could not save error to messages',
              description: persist.error,
              type: 'warning',
            });
          }
        };

        if (result?.success && result?.result != null) {
          const message =
            typeof result.result === 'object' && 'message' in result.result
              ? result.result.message
              : undefined;
          if (typeof message === 'string') {
            setAskResult(message);
          } else {
            await persistDocError(
              'Processor did not return a message',
              result?.instanceId,
            );
          }
        } else {
          await persistDocError(
            result?.error ?? 'Processor failed',
            result?.instanceId,
          );
        }
      } catch (error) {
        const description =
          error instanceof Error ? error.message : 'Unknown error';
        if (documentId) {
          const persist = await persistProcessorFailureAsDocumentRootMessage(
            apolloClient,
            {
              documentId,
              errorMessage: description,
              processorInstanceId: undefined,
              getMessagesVariables: documentMessagesVariables,
            },
          );
          if (!persist.ok) {
            toaster.create({
              title: 'Could not save error to messages',
              description: persist.error,
              type: 'warning',
            });
          }
        } else {
          toaster.create({
            title: 'Error',
            description: `Error: ${description}`,
            type: 'error',
          });
        }
      }
    },
    [apolloClient, documentId, documentMessagesVariables, executeProcessor],
  );

  const clearAskResult = React.useCallback(() => setAskResult(null), []);

  return {
    handleAskSubmit,
    askResult,
    clearAskResult,
    disabled,
    processingPrompt,
  };
}

/**
 * Inline AI prompt input for the document page footer (textarea + Apply).
 */
export function PromptFooterInput(props: PromptCommandBaseProps) {
  const { isNewDocument } = props;
  const [prompt, setPrompt] = React.useState('');
  const { handleSubmit, disabled, processingPrompt } = usePromptSubmit(props);

  const onSubmit = async () => {
    const text = prompt.trim();
    if (!text) return;
    setPrompt('');
    await handleSubmit(text);
  };

  return (
    <ChatComposerRow
      value={prompt}
      onChange={setPrompt}
      onSubmit={onSubmit}
      placeholder={
        isNewDocument
          ? 'Save the document first to use AI modifications'
          : 'e.g., Summarize this content, Translate to Spanish, Fix grammar errors...'
      }
      disabled={disabled}
      loading={processingPrompt}
    />
  );
}

/**
 * Prompt button (popover) for use where inline footer is not shown.
 */
export function PromptButton(props: PromptCommandBaseProps) {
  const { isNewDocument } = props;
  const [prompt, setPrompt] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const { handleSubmit, disabled, processingPrompt } = usePromptSubmit(props);

  const title = isNewDocument
    ? 'Save the document first to use AI modifications'
    : undefined;

  const onPopoverSubmit = async () => {
    if (!prompt.trim()) return;
    const promptText = prompt.trim();
    setPrompt('');
    setIsOpen(false);
    await handleSubmit(promptText);
  };

  const handleCancel = () => {
    setPrompt('');
    setIsOpen(false);
  };

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(e) => setIsOpen(e.open)}
      positioning={{ placement: 'bottom-start' }}
      size="sm"
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="AI prompt to edit document"
          title={title ?? 'Use AI to edit this document'}
          disabled={disabled}
          onClick={() => setIsOpen(true)}
        >
          {processingPrompt ? (
            <Spinner size="sm" />
          ) : (
            <FiZap style={{ width: '24px', height: '24px' }} />
          )}
        </button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            maxW="400px"
            p={4}
            bg="bg"
            border="1px solid"
            borderColor="border"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={3} align="stretch">
              <Text fontSize="sm" fontWeight="semibold" color="fg">
                AI Prompt
              </Text>
              <Text fontSize="xs" color="fg.muted">
                Provide instructions for how AI should modify this document
              </Text>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., Summarize this content, Translate to Spanish, Fix grammar errors..."
                rows={4}
                resize="vertical"
                minH="100px"
              />
              <HStack gap={2} justify="flex-end">
                <Button size="sm" variant="ghost" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={onPopoverSubmit}
                  disabled={!prompt.trim()}
                >
                  Apply
                </Button>
              </HStack>
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

export interface AIAssistModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string | undefined;
  isNewDocument: boolean;
}

type AssistMode = 'ask' | 'edit';

/**
 * AI Assist modal for the document header. Must be rendered inside EditDocumentProvider.
 * Toggle between Ask (returns AI message below) and Edit (applies edits to editor; save to persist).
 */
export function AIAssistModal({
  open,
  onOpenChange,
  documentId,
  isNewDocument,
}: AIAssistModalProps) {
  const { setValue, getValue } = useEditDocument();
  const [prompt, setPrompt] = React.useState('');
  const [mode, setMode] = React.useState<AssistMode>('ask');

  const onValueUpdate = React.useCallback(
    (value: EditorJsData | null) => {
      setValue(value);
      onOpenChange(false);
    },
    [setValue, onOpenChange],
  );

  const {
    handleSubmit,
    disabled: editDisabled,
    processingPrompt: editLoading,
  } = usePromptSubmit({
    documentId,
    isNewDocument,
    onValueUpdate,
    getEditorSnapshot: () => stringifyEditorJs(getValue()),
  });

  const {
    handleAskSubmit,
    askResult,
    clearAskResult,
    disabled: askDisabled,
    processingPrompt: askLoading,
  } = useAskSubmit({
    documentId,
    isNewDocument,
  });

  const isEditMode = mode === 'edit';
  const disabled = isEditMode ? editDisabled : askDisabled;
  const processingPrompt = isEditMode ? editLoading : askLoading;

  React.useEffect(() => {
    if (mode === 'edit') clearAskResult();
  }, [mode, clearAskResult]);

  const handleApply = React.useCallback(async () => {
    const text = prompt.trim();
    if (!text) return;
    setPrompt('');
    if (mode === 'edit') {
      await handleSubmit(text);
    } else {
      await handleAskSubmit(text);
    }
  }, [prompt, mode, handleSubmit, handleAskSubmit]);

  const handleClose = React.useCallback(() => {
    setPrompt('');
    clearAskResult();
    onOpenChange(false);
  }, [onOpenChange, clearAskResult]);

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      size="md"
    >
      <CenteredDialogContent>
        <DialogHeader>
          <DialogTitle>AI Assist</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={3} align="stretch">
            <HStack gap={2}>
              <Button
                size="sm"
                variant={mode === 'ask' ? 'solid' : 'outline'}
                colorScheme="blue"
                onClick={() => setMode('ask')}
              >
                Ask
              </Button>
              <Button
                size="sm"
                variant={mode === 'edit' ? 'solid' : 'outline'}
                colorScheme="blue"
                onClick={() => setMode('edit')}
              >
                Edit
              </Button>
            </HStack>
            <Text fontSize="sm" color="fg.muted">
              {mode === 'ask'
                ? 'Ask a question about this document. The answer will appear below.'
                : 'Describe how you want to modify this document. The edit will be applied in the editor; save to persist.'}
            </Text>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={
                isNewDocument
                  ? 'Save the document first to use AI'
                  : mode === 'ask'
                    ? 'e.g., What are the main points? Summarize the budget section...'
                    : 'e.g., Summarize this content, Translate to Spanish, Fix grammar errors...'
              }
              rows={4}
              resize="vertical"
              minH="100px"
              disabled={disabled}
            />
            {mode === 'ask' && askResult != null && (
              <Box
                p={3}
                borderRadius="md"
                bg="bg.muted"
                borderWidth="1px"
                borderColor="border"
                maxH="200px"
                overflowY="auto"
              >
                <Text fontSize="sm" whiteSpace="pre-wrap">
                  {askResult}
                </Text>
              </Box>
            )}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button
            colorScheme="blue"
            onClick={handleApply}
            disabled={!prompt.trim() || disabled}
            loading={processingPrompt}
            loadingText={mode === 'ask' ? 'Asking...' : 'Applying...'}
          >
            {mode === 'ask' ? 'Ask' : 'Apply'}
          </Button>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
